
import {defineComponent, computed, onMounted, onUnmounted, watch} from "@vue/runtime-core";
import {Marker} from 'leaflet';
import {useStore} from "@/store";
import {DynmapMarker, DynmapMarkerSet} from "@/dynmap";
import {ActionTypes} from "@/store/action-types";
import {createMarker, updateMarker} from "@/util/markers";
import DynmapLayerGroup from "@/leaflet/layer/DynmapLayerGroup";

export default defineComponent({
	props: {
		set: {
			type: Object as () => DynmapMarkerSet,
			required: true,
		},
		layerGroup: {
			type: Object as () => DynmapLayerGroup,
			required: true
		}
	},

	setup(props) {
		let updateFrame = 0;

		const store = useStore(),
			currentProjection = computed(() => store.state.currentProjection),
			pendingUpdates = computed(() => {
				const markerSetUpdates = store.state.pendingSetUpdates.get(props.set.id);

				return markerSetUpdates && markerSetUpdates.markerUpdates.length;
			}),
			layers = Object.freeze(new Map()) as Map<string, Marker>,

			createMarkers = () => {
				const projection = currentProjection.value;

				props.set.markers.forEach((marker: DynmapMarker, id: string) => {
					const layer = createMarker(marker, projection);

					layers.set(id, layer);
					props.layerGroup.addLayer(layer);
				});
			},

			deleteMarker = (id: string) => {
				let marker = layers.get(id) as Marker;

				if(!marker) {
					return;
				}

				props.layerGroup.removeLayer(marker);
				layers.delete(id);
			},

			handlePendingUpdates = () => {
				useStore().dispatch(ActionTypes.POP_MARKER_UPDATES, {
					markerSet: props.set.id,
					amount: 10,
				}).then(updates => {
					const projection = currentProjection.value;

					for(const update of updates) {
						if(update.removed) {
							deleteMarker(update.id);
						} else {
							const layer = updateMarker(layers.get(update.id), update.payload as DynmapMarker, projection);

							if(!layers.has(update.id)) {
								props.layerGroup.addLayer(layer);
							}

							layers.set(update.id, layer);
						}
					}

					if(pendingUpdates.value) {
						// eslint-disable-next-line no-unused-vars
						updateFrame = requestAnimationFrame(() => handlePendingUpdates());
					} else {
						updateFrame = 0;
					}
				});
			};

		//FIXME: Prevent unnecessary repositioning when changing worlds
		watch(currentProjection, () => {
			const projection = currentProjection.value;

			for (const [id, marker] of props.set.markers) {
				updateMarker(layers.get(id), marker, projection);
			}
		});

		watch(pendingUpdates, (newValue, oldValue) => {
			if(newValue && newValue > 0 && oldValue === 0 && !updateFrame) {
				updateFrame = requestAnimationFrame(() => handlePendingUpdates());
			}
		});

		onMounted(() => createMarkers());
		onUnmounted(() => updateFrame && cancelAnimationFrame(updateFrame));
	},

	render() {
		return null;
	}
});
